import { json } from "d3";
import { API_URL } from "./apiRoutes";
import { apiCall } from "./axiosService";
import { commonService } from "./commonService";

// src/utils/globalTracker.ts
export const globalClickTracker = () => {
    // ========== PAGE TIME TRACKING ==========
    let pageSession = {
        pageName: window.location.pathname,
        pageStartTime: new Date(),
        clicks: [],
        totalEvent: 0,
    };

    // ========== TRACK ALL EVENTS ==========
    const trackEvent = (type, details) => {
        const event = {
            type,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            currentPage: pageSession.pageName,
            ...details,
        };
    };

    // ========== PAGE TIME - WHEN ENTERING PAGE ==========
    const handlePageEnter = () => {
        const pageEnterEvent = {
            type: 'page_enter',
            page: pageSession.pageName,
            enterTime: new Date().toISOString(),
        };
    };

    // ========== PAGE TIME - WHEN LEAVING PAGE ==========
    const handlePageExit = () => {
        const pageExitTime = new Date();
        const timeSpentMS = pageExitTime - pageSession.pageStartTime;
        const timeSpentSeconds = Math.round(timeSpentMS / 1000);
        const timeSpentMinutes = (timeSpentSeconds / 60).toFixed(2);
        const asofDateElement = document.getElementById('asOfDate');
        const asofdate = asofDateElement?.value || '';
        const propertySelectElement = document.querySelector('.header-select-property #mainheaderpropertyselection');
        let propertyname = "";
        if (propertySelectElement) {
            propertyname = propertySelectElement.getAttribute("dataselected")
        }
        
        const pageSummary = {
            type: 'page_exit',
            page: pageSession.pageName,
            exitTime: pageExitTime.toISOString(),
            timeSpentSeconds: timeSpentSeconds,
            timeSpentMinutes: timeSpentMinutes,
            totalEvent: pageSession.totalEvent,
            eventDetails: pageSession.clicks,
            asofdate: asofdate,  // ← NEW: As-of date from header
            propertyname: propertyname,  // ← NEW: Property name from header select (selected text)
        };
        let IsNovaUser = false;
        let data = commonService.getLoginUserData();
        if (data) {
            if (data.username && data.username.toUpperCase() === "NOVA") {
                IsNovaUser = true;
            }
        }

        if (!IsNovaUser && window.location.href.toUpperCase().indexOf("LOCALHOST") < 0 && decodeURIComponent(pageSummary.page) !== "/" && decodeURIComponent(pageSummary.page).toUpperCase().indexOf("/LOGINSWITCH") < 0 && decodeURIComponent(pageSummary.page).toUpperCase().indexOf("/MARKETPLACE") < 0) {
            let page = decodeURIComponent(pageSummary.page);
            if (page) {
                if (page.toUpperCase().indexOf("/SNAPSHOT/") >= 0) {
                    const parts = page.split("/");
                    if (parts && parts.length > 3) {
                        page = `/${parts[1]}/${parts[2]}/${parts[3]}`;
                    }
                }

                if (page.toUpperCase().indexOf("/REVENUEGLANCE/") >= 0) {
                    page = `/revenue/revenueglance`;
                }

                if (page.toUpperCase().indexOf("/PORTFOLIOROLLUP/") >= 0) {
                    page = `/revenue/portfoliorollup`;
                }

                if (page.toUpperCase().indexOf("/PROPERTY-SETTING/") >= 0) {
                    page = `/revenue/property-setting`;
                }
            }
            apiCall(
                {
                    method: "POST",
                    url: API_URL.BASE_API_URL.Notification + API_URL.ACTIONLOG.SAVEPAGETRACKING,
                    body: {
                        pagetrackingid: 0,
                        asofdate: commonService.getDateInDBFormat(pageSummary.asofdate),
                        page: page,
                        propertyname: pageSummary.propertyname,
                        timeinsecond: pageSummary.timeSpentSeconds,
                        totalevent: pageSummary.totalEvent,
                        eventdetails: JSON.stringify(pageSummary.eventDetails),
                        sessionid: commonService.getItemWithoutCrypto("sessionid") ? commonService.getItemWithoutCrypto("sessionid") : ""
                    },
                },
                true
            );
        }
    };

    // ========== DETECT PAGE CHANGE ==========
    const checkPageChange = () => {
        const currentPage = window.location.pathname;
        if (currentPage !== pageSession.pageName) {
            // Page changed, exit old page
            handlePageExit();

            // Start new page session
            pageSession = {
                pageName: currentPage,
                pageStartTime: new Date(),
                clicks: [],
                totalEvent: 0,
            };
            handlePageEnter();
        }
    };

    // ========== CAPTURE ONLY CLICKABLE ELEMENTS (Button, Link, Input, Select) ==========
    const handleClick = (e) => {
        const target = e.target;
        const trackedElement = target.closest('button, a, input[type="text"], input[type="button"], input[type="submit"], [role="button"], [role="link"], .text-primary, [id]:not(#root):not(#datepicker-root):not(#contained-modal-title-vcenter):not([id^="detail-"])') || target;

        // Filter: Only track specific element types that have click events bound or have an ID
        const trackableElements = ['BUTTON', 'A', 'INPUT'];
        const elementTag = trackedElement.tagName;

        // Check if element or parent has text-primary class
        const hasTextPrimaryClass = trackedElement.classList.contains('text-primary') ||
            target.closest('.text-primary');

        // Check if element has a valid tracking ID (ignoring generic containers)
        const hasId = !!(trackedElement.id && trackedElement.id !== 'root' && trackedElement.id !== 'datepicker-root' && trackedElement.id !== 'contained-modal-title-vcenter' && !trackedElement.id.startsWith('detail-'));

        // Skip if not a trackable element AND doesn't have text-primary class AND doesn't have an ID
        if (!trackableElements.includes(elementTag) && !hasTextPrimaryClass && !hasId) {
            return;
        }

        // Skip text nodes and decorative elements (span, i, svg) unless they have text-primary or an ID
        if (!hasTextPrimaryClass && !hasId && (!trackedElement.tagName || target.tagName === 'SPAN' || target.tagName === 'I' || target.tagName === 'SVG')) {
            return;
        }

        // Get click position on page
        const clickX = e.pageX;
        const clickY = e.pageY;

        // Get element position
        const rect = trackedElement.getBoundingClientRect();
        const elementX = rect.left + window.scrollX;
        const elementY = rect.top + window.scrollY;

        // Get snapshotrow rowtitle attribute as sectionTitle
        let sectionTitle = '';
        const snapshotRowElement = trackedElement.closest('.snapshotrow');
        if (snapshotRowElement) {
            sectionTitle = snapshotRowElement.getAttribute('rowtitle') || '';
        }



        pageSession.totalEvent++;

        const clickInfo = {
            clickNumber: pageSession.totalEvent,
            elementType: elementTag,
            id: trackedElement.id || 'no-id',
            sectionTitle: sectionTitle,
        };

        pageSession.clicks.push(clickInfo);

        trackEvent('click', {
            elementType: elementTag,
            id: trackedElement.id || 'no-id',
            value: trackedElement.value || undefined,
            clickNumber: pageSession.totalEvent,
            sectionTitle: sectionTitle,
        });
    };

    // ========== CAPTURE DROPDOWN CHANGES ==========
    const handleChange = (e) => {
        const target = e.target;

        if (target.tagName === 'SELECT' || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
            // Get snapshotrow rowtitle attribute as sectionTitle
            let sectionTitle = '';
            const snapshotRowElement = target.closest('.snapshotrow');
            if (snapshotRowElement) {
                sectionTitle = snapshotRowElement.getAttribute('rowtitle') || '';
            }

            // Get asofDate value from header
            const asofDateElement = document.getElementById('asOfDate');
            const asofdate = asofDateElement?.value || '';

            pageSession.totalEvent++;

            const changeInfo = {
                clickNumber: pageSession.totalEvent,
                eventType: 'change',  // ← Distinguish from click
                elementType: target.tagName,
                fieldName: target.name || target.id,
                fieldValue: target.value,
                id: target.id || 'no-id',
                sectionTitle: sectionTitle,
            };

            pageSession.clicks.push(changeInfo);

            trackEvent('change', {
                field: target.name || target.id,
                value: target.value,
                type: target.type || target.tagName.toLowerCase(),
                sectionTitle: sectionTitle,
            });
        }
    };

    // ========== CAPTURE FORM SUBMISSIONS ==========
    const handleSubmit = (e) => {
        const form = e.target;
        trackEvent('form_submit', {
            formId: form.id || form.name,
            action: form.getAttribute('action') || 'submit',
        });
    };

    // ========== LISTEN FOR PAGE EXIT ==========
    const handleBeforeUnload = () => {
        handlePageExit();
    };

    // ========== INTERCEPT ROUTE CHANGES ==========
    const originalPushState = window.history.pushState;
    window.history.pushState = function (...args) {

        //handlePageExit(); // Save previous page stats
        const result = originalPushState.apply(this, args);
        setTimeout(() => {
            checkPageChange();
        }, 100);
        return result;
    };

    // ========== SETUP EVENT LISTENERS ==========
    document.addEventListener('click', handleClick, true);
    document.addEventListener('change', handleChange, true);
    document.addEventListener('submit', handleSubmit, true);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', checkPageChange);

    // Initial page enter
    handlePageEnter();

    return () => {
        document.removeEventListener('click', handleClick, true);
        document.removeEventListener('change', handleChange, true);
        document.removeEventListener('submit', handleSubmit, true);
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('popstate', checkPageChange);
        window.history.pushState = originalPushState;
    };
};